import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  MapPin,
  ChevronDown,
  List,
  Map as MapIcon,
  SlidersHorizontal,
  X,
  CheckCircle,
  Target,
  Calendar,
  Eye,
} from 'lucide-react';
import { Select, MenuItem } from '@mui/material';
import { FranchiseFilter } from '../../components/franchise/FranchiseFilter';
import { useFranchiseRequests } from '@/hooks/useFranchise';
import { Spinner, EmptyState, Pagination } from '@/components/common';
import { cn } from '@/lib/utils';
import FranchiseRequestCard from '../../components/card/FranchiseRequestCard';
import type { FranchiseRequestFilter, FranchiseRequest } from '@/types';
import { formatNumber } from '@/utils';

/* ─── helpers ─── */
function fmtBudget(min: string, max: string) {
  const fmtM = (s: string) => {
    const n = parseFloat(s);
    return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(0)}tr` : formatNumber(n);
  };
  return `${fmtM(min)} – ${fmtM(max)}`;
}
function typeEmoji(t: string) {
  const m: Record<string, string> = {
    RESTAURANT: '🍽️',
    CAFE: '☕',
    BAR: '🍸',
    BAKERY: '🥐',
    'FOOD COURT': '🏪',
    LOUNGE: '🍸',
    COFFEE_SHOP: '☕',
    FINE_DINING: '🍽️',
  };
  return m[t?.toUpperCase()] ?? '🏢';
}

function getPrimaryImg(images: any[]) {
  if (!images?.length) return null;
  return (images.find((i) => i.isPrimary) ?? images[0])?.image?.publicUrl ?? null;
}

const PROPERTY_TYPES = [
  { value: '', label: 'Tất cả loại' },
  { value: 'RESTAURANT', label: 'Nhà hàng' },
  { value: 'CAFE', label: 'Cafe' },
  { value: 'BAR', label: 'Bar / Lounge' },
  { value: 'BAKERY', label: 'Tiệm bánh' },
];
const BUDGET_RANGES = [
  { label: 'Tất cả', min: undefined, max: undefined },
  { label: '< 20tr', min: undefined, max: 20_000_000 },
  { label: '20–50tr', min: 20_000_000, max: 50_000_000 },
  { label: '50–100tr', min: 50_000_000, max: 100_000_000 },
  { label: '> 100tr', min: 100_000_000, max: undefined },
];
const LIMIT = 9;

const REQUIRE_FLAGS = [
  { key: 'requiresParking', label: 'Đỗ xe' },
  { key: 'requiresStorage', label: 'Kho' },
  { key: 'requiresGasConnection', label: 'Gas' },
  { key: 'requiresHoodSystem', label: 'Hút khói' },
  { key: 'requiresGreaseTrap', label: 'Bẫy mỡ' },
  { key: 'requiresLiquorLicense', label: 'Phép rượu' },
  { key: 'requiresOutdoorSeating', label: 'Ngoài trời' },
  { key: 'requiresCustomerBathroom', label: 'WC khách' },
];

/* ─── Map ─── */
function RequestMap({
  items,
  selected,
  onSelect,
}: {
  items: FranchiseRequest[];
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const clusterGroupRef = useRef<any>(null);
  const routeLayerRef = useRef<any>(null);
  const routeLabelsRef = useRef<any[]>([]);

  const [showRoute, setShowRoute] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);

  const jitteredItems = useMemo(() => {
    return items.map((item) => {
      let lat = parseFloat(item.latitude);
      let lng = parseFloat(item.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        const provinceName = item.province?.name?.toLowerCase() || '';
        if (provinceName.includes('hà nội')) { lat = 21.0285; lng = 105.8542; }
        else if (provinceName.includes('đà nẵng')) { lat = 16.0544; lng = 108.2022; }
        else { lat = 10.7769; lng = 106.7009; }
      }

      const latOffset = (Math.random() - 0.5) * 0.02;
      const lngOffset = (Math.random() - 0.5) * 0.02;

      return {
        ...item,
        renderLat: lat + latOffset,
        renderLng: lng + lngOffset,
      };
    });
  }, [items]);

  useEffect(() => {
    const loadCss = (id: string, href: string) => {
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id; link.rel = 'stylesheet'; link.href = href;
        document.head.appendChild(link);
      }
    };
    loadCss('leaflet-css', 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    loadCss('markercluster-css', 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css');
    loadCss('markercluster-default-css', 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css');

    const loadScript = (src: string, isLoaded: () => boolean) => {
      return new Promise((resolve) => {
        if (isLoaded()) return resolve(true);
        let script = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement;
        if (script) {
          const interval = setInterval(() => {
            if (isLoaded()) {
              clearInterval(interval);
              resolve(true);
            }
          }, 50);
          script.addEventListener('load', () => {
            clearInterval(interval);
            resolve(true);
          });
          return;
        }
        script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(true);
        document.head.appendChild(script);
      });
    };

    const initMap = async () => {
      await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js', () => !!(window as any).L);
      await loadScript('https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js', () => !!(window as any).L?.markerClusterGroup);

      const L = (window as any).L;
      if (!L || !mapRef.current || mapInstanceRef.current) return;

      const map = L.map(mapRef.current, { zoomControl: true }).setView([10.7769, 106.7009], 12);
      L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
        attribution: '© Google Maps',
        maxZoom: 20,
      }).addTo(map);

      mapInstanceRef.current = map;
      renderMarkers(L, map);
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const observer = new ResizeObserver(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    });
    observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  const renderMarkers = (L: any, map: any) => {
    if (clusterGroupRef.current) map.removeLayer(clusterGroupRef.current);

    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      iconCreateFunction: function (cluster: any) {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="background:#f97316;color:#fff;border:2px solid #fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${count}</div>`,
          className: '',
          iconSize: [36, 36],
        });
      }
    });

    jitteredItems.forEach((item) => {
      const lat = item.renderLat;
      const lng = item.renderLng;
      if (!lat || !lng) return;

      const isSelected = item.id === selected;
      const priceStr = fmtBudget(item.budgetMin, item.budgetMax);

      const icon = L.divIcon({
        className: '',
        html: `<div style="
          background: #f97316;
          color: #fff;
          border: ${isSelected ? '2px solid #000' : 'none'};
          border-radius: 20px;
          padding: 4px 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          white-space: nowrap;
          width: max-content;
          position: relative;
          z-index: ${isSelected ? 1000 : 1};
          transition: all 0.2s ease;
          transform: ${isSelected ? 'scale(1.1)' : 'scale(1)'};
        ">
          ${priceStr}
          <div style="position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);border-width:6px 6px 0;border-style:solid;border-color:${isSelected ? '#000' : '#f97316'} transparent transparent transparent;"></div>
          ${isSelected ? `<div style="position:absolute;bottom:-3px;left:50%;transform:translateX(-50%);border-width:4px 4px 0;border-style:solid;border-color:#f97316 transparent transparent transparent;"></div>` : ''}
        </div>`,
        iconSize: null as any,
        iconAnchor: [20, 20],
      });

      const m = L.marker([lat, lng], { icon })
        .bindPopup(
          `
          <a href="/nhu-cau-nhuong-quyen/${item.id}" style="display:block; width: 220px; text-decoration: none; color: inherit; font-family: inherit;">
            <div style="position: relative; border-radius: 8px; overflow: hidden; height: 140px; margin-bottom: 8px; background: #fff7ed; display: flex; align-items: center; justify-content: center; font-size: 40px;">
              ${getPrimaryImg(item.images || []) ? `<img src="${getPrimaryImg(item.images || [])}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;" />` : '🔍'}
              <span style="position: absolute; top: 8px; left: 8px; background: #f97316; color: #fff; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold; z-index: 10;">
                ${item.approvalStatus === 'approved' ? 'Đã duyệt' : 'Chờ duyệt'}
              </span>
            </div>
            <div style="display:flex; align-items: center; gap: 4px; margin-bottom: 4px;">
               <span style="background: #f3f4f6; color: #4b5563; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase;">${item.desiredPropertyType || 'Mặt bằng'}</span>
            </div>
            <p style="font-weight:700; font-size:13px; margin:0 0 4px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; color: #111827;">
              Cần nhượng quyền ${[item.desiredPropertyType, item.desiredBusinessType].filter(Boolean).join(", ")}
            </p>
            <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px;">
              <p style="color:#f97316; font-weight:800; font-size:16px; margin:0;">${priceStr}</p>
            </div>
            <p style="font-size:11px; color:#6b7280; margin:0 0 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              ${item.district?.name}, ${item.province?.name}
            </p>
            <div style="border-top: 1px solid #f3f4f6; padding-top: 8px; display: flex; gap: 12px; font-size: 11px; color: #6b7280; align-items: center;">
              ${item.requiredArea ? `<span style="display: flex; align-items: center; gap: 4px;">📐 ${item.requiredArea}m²</span>` : ''}
            </div>
          </a>
        `,
          { maxWidth: 220, offset: [0, -10] }
        )
        .on('click', () => onSelect(item.id));

      clusterGroup.addLayer(m);
    });

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;

    if (items.length && !showRoute) {
      const lats = items.map((i) => parseFloat(i.latitude)).filter(Boolean);
      const lngs = items.map((i) => parseFloat(i.longitude)).filter(Boolean);
      if (lats.length) {
        map.fitBounds(
          [
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)],
          ],
          { padding: [40, 40], maxZoom: 14 }
        );
      }
    }
  };

  useEffect(() => {
    const L = (window as any).L;
    if (L && mapInstanceRef.current && L.markerClusterGroup) {
      renderMarkers(L, mapInstanceRef.current);
    }
  }, [items, selected]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const L = (window as any).L;
    if (!map || !L) return;

    const clearRoute = () => {
      if (routeLayerRef.current) { map.removeLayer(routeLayerRef.current); routeLayerRef.current = null; }
      routeLabelsRef.current.forEach(label => map.removeLayer(label));
      routeLabelsRef.current = [];
    };

    if (!showRoute || jitteredItems.length < 2) {
      clearRoute();
      return;
    }

    const fetchRoute = async () => {
      setRouteLoading(true);
      clearRoute();
      try {
        const pointsToRoute = jitteredItems.slice(0, 50);
        const coordinates = pointsToRoute.map(item => `${item.renderLng},${item.renderLat}`).join(';');
        const url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&annotations=true`;

        const res = await fetch(url);
        const data = await res.json();

        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          const route = data.routes[0];

          const geoJsonLayer = L.geoJSON(route.geometry, {
            style: { color: '#f97316', weight: 6, opacity: 0.9 }
          }).addTo(map);
          routeLayerRef.current = geoJsonLayer;

          route.legs.forEach((leg: any, index: number) => {
            const startNode = pointsToRoute[index];
            const endNode = pointsToRoute[index + 1];
            const midLat = (startNode.renderLat + endNode.renderLat) / 2;
            const midLng = (startNode.renderLng + endNode.renderLng) / 2;
            const durationMins = Math.round(leg.duration / 60);
            const distanceKm = (leg.distance / 1000).toFixed(1);

            const labelIcon = L.divIcon({
              className: '',
              html: `<div style="background:#f97316;color:white;padding:4px 10px;border-radius:12px;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,0.3);position:relative;">${durationMins} phút<div style="position:absolute;bottom:-4px;left:50%;transform:translateX(-50%);border-width:5px 5px 0;border-style:solid;border-color:#f97316 transparent transparent transparent;"></div></div>`,
              iconSize: null,
            });

            const marker = L.marker([midLat, midLng], { icon: labelIcon, zIndexOffset: 500 }).addTo(map);
            routeLabelsRef.current.push(marker);
          });
          map.fitBounds(geoJsonLayer.getBounds(), { padding: [40, 40] });
        }
      } catch (err) {
        console.error("Failed to fetch route:", err);
      } finally {
        setRouteLoading(false);
      }
    };

    fetchRoute();
  }, [showRoute, jitteredItems]);

  return (
    <div className="absolute inset-0 w-full h-full rounded-xl overflow-hidden" style={{ minHeight: 400 }}>
      <div ref={mapRef} className="absolute inset-0 z-0" />
      <div className="absolute top-4 right-4 z-[400]">
        <button
          onClick={() => setShowRoute(!showRoute)}
          disabled={jitteredItems.length < 2 || routeLoading}
          className={`px-4 py-2 rounded-lg font-medium shadow-md transition-all flex items-center gap-2 ${showRoute ? 'bg-orange-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          {routeLoading ? (
            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
          ) : (
            <span>📍</span>
          )}
          {showRoute ? 'Ẩn lộ trình' : 'Hiển thị lộ trình'}
        </button>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function FranchiseRequestListPage() {
  const [filters, setFilters] = useState<FranchiseRequestFilter>({
    page: 1,
    limit: LIMIT,
    orderBy: 'createdAt',
    orderDirection: 'DESC',
    language: 'vi',
  });
  const [viewMode, setViewMode] = useState<'split' | 'list' | 'map'>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useFranchiseRequests(filters);
  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const handleFilterSearch = (newFilters: any) => {
    setFilters((f) => ({
      limit: f.limit,
      orderBy: f.orderBy,
      orderDirection: f.orderDirection,
      language: f.language,
      ...newFilters,
      page: 1,
    }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="section-label">Nhận nhượng quyền</div>
        <div className="flex items-end justify-between flex-wrap gap-2">
          <h1 className="section-title mb-0">Yêu cầu nhượng quyền</h1>
          <span className="text-sm" style={{ color: 'var(--text-3)' }}>
            {total} yêu cầu
          </span>
        </div>
      </div>

      {/* Filter panel */}
      <div className="sticky top-[104px] z-30 mb-8">
        <FranchiseFilter onSearch={handleFilterSearch} initialFilters={filters} />
      </div>

      <div className="flex items-center justify-end mb-4 gap-4">
        {/* Sort options aligned right */}
        <Select
          value={`${filters.orderBy}|${filters.orderDirection}`}
          onChange={(e) => {
            const [orderBy, orderDirection] = (e.target.value as string).split('|') as [any, 'ASC' | 'DESC'];
            setFilters(prev => ({ ...prev, orderBy, orderDirection, page: 1 }));
          }}
          size="small"
          sx={{
            minWidth: '160px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--brand)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--brand)',
            }
          }}
        >
          <MenuItem value="createdAt|DESC" sx={{ fontSize: '14px' }}>Mới nhất</MenuItem>
          <MenuItem value="budgetMax|DESC" sx={{ fontSize: '14px' }}>Ngân sách cao nhất</MenuItem>
          <MenuItem value="requiredArea|DESC" sx={{ fontSize: '14px' }}>Diện tích lớn nhất</MenuItem>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <EmptyState title="Không có yêu cầu nhượng quyền nào" description="Thử thay đổi bộ lọc" icon="🔍" />
      ) : (
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <div
            className={cn(
              'grid gap-4 transition-opacity grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
              isFetching && 'opacity-60',
            )}
          >
            {items.map((item) => (
              <FranchiseRequestCard key={item.id} item={item} isSelected={selectedId === item.id} onMouseEnter={() => setSelectedId(item.id)} />
            ))}
          </div>
          <Pagination
            page={filters.page ?? 1}
            totalPages={totalPages}
            onChange={(p) => setFilters((f) => ({ ...f, page: p }))}
          />
        </div>
      )}
    </div>
  );
}

